import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { authService } from "../../services/auth.service";
import { useAuth } from "../../context/AuthContext";
import { loginSchema } from "./validations/auth.schema";
import Mascot from "../../components/brand/Mascot";
import DoodleUnderline from "../../components/brand/DoodleUnderline";

const Login = () => {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const { setUser, isAuthenticated, loading } = useAuth();
  const from = (useLocation().state as { from?: string } | null)?.from;
  const [failureKey, setFailureKey] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(loginSchema) });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFailureKey(null);
    try {
      const result = await authService.login(email, password);
      if (result.requires2fa) {
        // No cookies yet — the session starts once the emailed code checks out.
        navigate("/verify-otp", {
          state: { tempToken: result.tempToken, email, purpose: "login", from },
        });
        return;
      }
      setUser(result.user);
      navigate(from ?? "/account", { replace: true });
    } catch (err) {
      // Login is the one flow with no business tag to branch on: a bad
      // password and a non-student role both answer a bare 401, so the
      // status is all there is.
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401) setFailureKey("errors.invalid_credentials");
      else if (status === 403) setFailureKey("errors.account_inactive");
      else setFailureKey("common:errors.generic");
    }
  });

  // Already signed in: the login form has nothing to offer, go to the account page.
  if (!loading && isAuthenticated) return <Navigate to={from ?? "/account"} replace />;

  return (
    <div className="mx-auto mt-8 grid max-w-4xl overflow-hidden rounded-xl3 border border-hairline shadow-lift sm:grid-cols-[1.05fr_.95fr]">
      <aside
        aria-hidden="true"
        className="hidden bg-hero p-12 sm:flex sm:flex-col"
      >
        <span className="grid h-10 w-10 -rotate-3 place-items-center rounded-xl bg-apricot text-lg font-extrabold text-white">
          學
        </span>
        <p className="mt-8 font-display text-3xl font-extrabold text-ink">
          {t("marketing:home.hero.title")}
        </p>
        <Mascot className="mt-6 w-32" />
        <DoodleUnderline className="h-3 w-40 text-teal" />
      </aside>

      <div className="bg-surface p-10">
        <h1 className="mb-6 font-display text-2xl font-extrabold">
          {t("login.title")}
        </h1>

        {failureKey && (
          <p
            role="alert"
            className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            {t(failureKey)}
          </p>
        )}

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-bold">
              {t("fields.email")}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className="field-input"
              {...register("email")}
            />
            {errors.email?.message && (
              <p className="mt-1 text-sm text-red-600">
                {t(errors.email.message)}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="mb-1 block text-sm font-bold"
            >
              {t("fields.password")}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="field-input"
              {...register("password")}
            />
            {errors.password?.message && (
              <p className="mt-1 text-sm text-red-600">
                {t(errors.password.message)}
              </p>
            )}
          </div>

          <button type="submit" disabled={isSubmitting} className="btn w-full">
            {t("login.submit")}
          </button>
        </form>

        <p className="mt-6 text-sm">
          <Link to="/forgot-password" className="font-bold text-teal-dark underline">
            {t("login.forgot")}
          </Link>
        </p>
        <p className="mt-2 text-sm">
          {t("login.noAccount")}{" "}
          <Link to="/register" className="font-bold text-teal-dark underline">
            {t("register.submit")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
